import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sun, Moon, Menu, X, BookOpen, Users, Target, UserPlus,
  HeartHandshake, Phone, UserCheck, IndianRupee, ShieldCheck, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import logoImage from '@/images/meenammalogobg.png';

// ── Constants ───────────────────────────────────────────────────────────────
const NAV_LINKS = [
  { href: '#about',                  label: 'About Us',     Icon: BookOpen      },
  { href: '#mission',                label: 'Mission',      Icon: Target        },
  { href: '#programs',               label: 'Programs',     Icon: Users         },
  { href: '#admissions',             label: 'Admissions',   Icon: UserPlus      },
  { href: '#scholarships',           label: 'Scholarships', Icon: UserCheck     },
  { href: '#impact',                 label: 'Support Us',   Icon: HeartHandshake},
  { href: '#sponsorship-highlights', label: 'Spotlights',   Icon: IndianRupee   },
  { href: '#contact',                label: 'Contact',      Icon: Phone         },
];

const HEADER_HEIGHT = 88;

// ── Logo ────────────────────────────────────────────────────────────────────
const Logo = ({ onNavigate }) => (
  <a
    href="#"
    className="flex items-center gap-2.5 flex-shrink-0 group"
    onClick={(e) => onNavigate(e, '#hero')}
    aria-label="Meenamma Educational Trust – home"
  >
    <motion.img
      src={logoImage}
      alt="Meenamma Educational Trust"
      className="h-14 lg:h-16 w-auto drop-shadow-sm"
      whileHover={{ scale: 1.05, rotate: -1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    />
  </a>
);

// ── Nav Item ────────────────────────────────────────────────────────────────
const NavItem = ({ link, onNavigate, index, isActive }) => {
  const { href, label, Icon } = link;
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
    >
      <a
        href={href}
        onClick={(e) => onNavigate(e, href)}
        className={`relative flex items-center gap-1.5 px-2.5 py-2 text-sm font-semibold
                    transition-colors duration-200 whitespace-nowrap group rounded-lg
                    ${isActive
                      ? 'text-primary bg-primary/6'
                      : 'text-foreground/65 hover:text-primary hover:bg-primary/4'
                    }`}
        aria-label={`Navigate to ${label} section`}
        aria-current={isActive ? 'page' : undefined}
      >
        <Icon className={`w-3.5 h-3.5 transition-opacity ${isActive ? 'opacity-100' : 'opacity-50 group-hover:opacity-100'}`} />
        {label}
        {/* Animated underline */}
        <span
          className={`absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-primary
                      transition-all duration-300
                      ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
        />
      </a>
    </motion.div>
  );
};

// ── Main Header ─────────────────────────────────────────────────────────────
const Header = () => {
  const [isMenuOpen,     setIsMenuOpen]     = useState(false);
  const [isScrolled,     setIsScrolled]     = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection,  setActiveSection]  = useState('');
  const [darkMode,    setDarkMode]    = useState(() => {
    if (typeof window === 'undefined') return false;
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Apply theme
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Track scroll + active section
  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 24);

      // Scroll progress (0–1)
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? window.scrollY / docHeight : 0);

      // Detect active section via IntersectionObserver fallback
      const sectionIds = NAV_LINKS.map((l) => l.href.replace('#', ''));
      let current = '';
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const { top } = el.getBoundingClientRect();
          if (top <= HEADER_HEIGHT + 40) current = id;
        }
      }
      setActiveSection(current);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menu on resize
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1024) setIsMenuOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const navigateTo = useCallback((e, href) => {
    e.preventDefault();
    setIsMenuOpen(false);
    const id = href.replace('#', '');
    if (id === 'hero') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - HEADER_HEIGHT, behavior: 'smooth' });
    }
  }, []);

  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300
        ${isScrolled ? 'shadow-lg shadow-foreground/5' : ''}`}
      animate={{
        backgroundColor: isScrolled
          ? 'hsl(var(--background)/0.94)'
          : 'hsl(var(--background)/0.55)',
        backdropFilter: isScrolled ? 'blur(18px) saturate(1.6)' : 'blur(0px)',
      }}
      transition={{ duration: 0.25 }}
    >
      {/* Scroll progress bar */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-[2.5px] brand-gradient-bg origin-left pointer-events-none z-50"
        style={{ scaleX: scrollProgress }}
        aria-hidden="true"
      />

      {/* Gradient bottom border — replaces flat border-b */}
      <AnimatePresence>
        {isScrolled && (
          <motion.div
            className="absolute bottom-0 left-0 right-0 h-px pointer-events-none"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, hsl(var(--brand-indigo)/0.45) 30%, hsl(var(--brand-emerald)/0.45) 70%, transparent 100%)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-[72px] gap-3">

          <Logo onNavigate={navigateTo} />

          {/* Desktop nav */}
          <nav
            className="hidden lg:flex items-center gap-0.5 xl:gap-1 flex-1 justify-center"
            role="navigation"
            aria-label="Main navigation"
          >
            {NAV_LINKS.map((link, i) => (
              <NavItem
                key={link.href}
                link={link}
                onNavigate={navigateTo}
                index={i}
                isActive={activeSection === link.href.replace('#', '')}
              />
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-1.5 flex-shrink-0">

            {/* Apply Now CTA (desktop) */}
            <Button
              variant="default"
              size="sm"
              asChild
              className="hidden lg:inline-flex items-center gap-1.5 text-xs px-4"
            >
              <a href="#admissions" onClick={(e) => navigateTo(e, '#admissions')}>
                <Sparkles className="h-3 w-3" />
                Apply Now
              </a>
            </Button>

            {/* Admin (desktop) */}
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="hidden lg:flex text-muted-foreground hover:text-primary opacity-35 hover:opacity-85"
            >
              <a href="/admin" aria-label="Admin portal" title="Admin Portal">
                <ShieldCheck className="h-4.5 w-4.5" />
              </a>
            </Button>

            {/* Dark mode toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDarkMode(!darkMode)}
              aria-label={`Switch to ${darkMode ? 'light' : 'dark'} mode`}
              className="text-foreground/70 hover:text-primary"
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={darkMode ? 'sun' : 'moon'}
                  initial={{ rotate: -30, opacity: 0, scale: 0.8 }}
                  animate={{ rotate: 0, opacity: 1, scale: 1 }}
                  exit={{ rotate: 30, opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.18 }}
                >
                  {darkMode ? <Sun className="h-4.5 w-4.5" /> : <Moon className="h-4.5 w-4.5" />}
                </motion.span>
              </AnimatePresence>
            </Button>

            {/* Hamburger (mobile) */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={isMenuOpen ? 'close' : 'open'}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </motion.span>
              </AnimatePresence>
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
            className="lg:hidden overflow-hidden bg-background/97 backdrop-blur-xl
                       border-t border-border/40 shadow-xl"
            role="navigation"
            aria-label="Mobile navigation"
          >
            <nav className="flex flex-col gap-0.5 px-4 py-4">
              {NAV_LINKS.map(({ href, label, Icon }, i) => {
                const isActive = activeSection === href.replace('#', '');
                return (
                  <motion.a
                    key={href}
                    href={href}
                    onClick={(e) => navigateTo(e, href)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold
                               transition-all duration-200
                               ${isActive
                                 ? 'text-primary bg-primary/8 border border-primary/12'
                                 : 'text-foreground/75 hover:text-primary hover:bg-primary/5'
                               }`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.035, duration: 0.25 }}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-primary' : ''}`} />
                    {label}
                    {isActive && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary" />
                    )}
                  </motion.a>
                );
              })}

              {/* Apply Now mobile */}
              <motion.a
                href="#admissions"
                onClick={(e) => navigateTo(e, '#admissions')}
                className="flex items-center justify-center gap-2 mx-1 mt-3 px-4 py-3
                           rounded-xl bg-primary text-primary-foreground text-sm font-semibold
                           shadow-md shadow-primary/25 hover:bg-primary/92 transition-all"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.32 }}
              >
                <Sparkles className="h-4 w-4" />
                Apply Now
              </motion.a>

              {/* Admin link mobile */}
              <motion.a
                href="/admin"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-medium
                           text-muted-foreground hover:text-primary hover:bg-primary/5
                           transition-all mt-1 border-t border-border/30 pt-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.38 }}
              >
                <ShieldCheck className="w-4 h-4" />
                Admin Portal
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
